import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'nav_home' },
    { path: '/about', label: 'nav_about' },
    { path: '/events', label: 'nav_events' },
    { path: '/archive', label: 'nav_archive' },
    { path: '/membership', label: 'nav_membership' },
    { path: '/contact', label: 'nav_contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 glass border-b border-black/5" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <nav className="h-20 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="/logo-bunt.jpg" 
              alt="Amitalia e.V." 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label}`}
                className={`font-body text-sm tracking-wide transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-ferrari font-semibold'
                    : 'text-foreground/70 hover:text-foreground'
                }`}
              >
                {t(link.label)}
              </Link>
            ))}
          </div>

          {/* Language Switcher & Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Language Flags */}
            <div className="flex items-center gap-2" data-testid="language-switcher">
              <button
                onClick={() => setLanguage('de')}
                className={`flag-btn ${language === 'de' ? 'active' : ''}`}
                data-testid="lang-de-btn"
                aria-label="Deutsch"
              >
                <svg viewBox="0 0 32 32" className="w-full h-full">
                  <rect y="0" width="32" height="10.67" fill="#000000"/>
                  <rect y="10.67" width="32" height="10.67" fill="#DD0000"/>
                  <rect y="21.33" width="32" height="10.67" fill="#FFCC00"/>
                </svg>
              </button>
              <button
                onClick={() => setLanguage('it')}
                className={`flag-btn ${language === 'it' ? 'active' : ''}`}
                data-testid="lang-it-btn"
                aria-label="Italiano"
              >
                <svg viewBox="0 0 32 32" className="w-full h-full">
                  <rect x="0" width="10.67" height="32" fill="#009246"/>
                  <rect x="10.67" width="10.67" height="32" fill="#FFFFFF"/>
                  <rect x="21.33" width="10.67" height="32" fill="#CE2B37"/>
                </svg>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-black/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block font-body text-base transition-colors ${
                    isActive(link.path)
                      ? 'text-ferrari font-semibold'
                      : 'text-foreground/70'
                  }`}
                >
                  {t(link.label)}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gradient Line */}
      <div className="gradient-line" />
    </header>
  );
};

export default Navigation;
